// File: frontend/src/components/pages/home/Features.js

import React from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../hooks/useLanguage';
import { MessageCircle, FileCheck, Bell, Shield, CheckCircle, AlertTriangle, Clock, Send, ChevronRight, Car, Home as HomeIcon, Heart, ArrowRight, TrendingDown, Calendar } from 'lucide-react';
import ScrollAnimation from '../../common/ScrollAnimation';
import AuthModal from '../../auth/AuthModal';
import styles from './Features.module.css';

/* ============================================
   MOCKUP: Policy Chat — Real Conversation
   ============================================ */
const MockupPolicyChat = ({ t }) => (
  <div className={styles.mockup}>
    {/* Chat header */}
    <div className={styles.mChatHeader}>
      <div className={styles.mChatAvatar}>
        <MessageCircle size={14} strokeWidth={2} />
      </div>
      <div>
        <span className={styles.mChatName}>{t('features.mock1.chat_name', 'Policy Assistant')}</span>
        <span className={styles.mChatOnline}>
          <span className={styles.mOnlineDot} />
          {t('features.mock1.chat_status', 'Analyzing your TD Auto Policy')}
        </span>
      </div>
    </div>

    <div className={styles.mChatBody}>
      {/* User message */}
      <div className={styles.mChatRow}>
        <div className={styles.mChatRight}>
          <div className={styles.mBubbleUser}>
            <p>{t('features.mock1.user_q', 'Is water damage from a burst pipe covered under my home insurance?')}</p>
          </div>
          <span className={styles.mChatTime}>11:14 AM</span>
        </div>
      </div>

      {/* AI response */}
      <div className={styles.mChatRow}>
        <div className={styles.mChatLeft}>
          <div className={styles.mBubbleAI}>
            <p className={styles.mBubbleText}>
              {t('features.mock1.ai_intro', 'Based on your Intact Home Protection Plan, here\'s what I found:')}
            </p>

            <div className={styles.mCoverageCard}>
              <div className={styles.mCoverageHeader}>
                <CheckCircle size={12} strokeWidth={2.5} />
                <span className={styles.mCoverageYes}>{t('features.mock1.covered', 'Yes, this is covered')}</span>
              </div>
              <p className={styles.mCoverageText}>
                {t('features.mock1.ai_detail', 'Sudden and accidental water damage from burst pipes is covered under your "Water Damage" endorsement.')}
              </p>
            </div>

            <div className={styles.mDetailGrid}>
              <div className={styles.mDetailItem}>
                <span className={styles.mDetailLabel}>{t('features.mock1.limit_label', 'Coverage Limit')}</span>
                <span className={styles.mDetailValue}>$250,000</span>
              </div>
              <div className={styles.mDetailItem}>
                <span className={styles.mDetailLabel}>{t('features.mock1.deductible_label', 'Deductible')}</span>
                <span className={styles.mDetailValue}>$1,000</span>
              </div>
            </div>

            <div className={styles.mWarningBox}>
              <AlertTriangle size={11} strokeWidth={2.5} />
              <span>{t('features.mock1.ai_warning', 'Note: Gradual water damage (e.g., slow leaks) is excluded. Only sudden events are covered.')}</span>
            </div>
          </div>
          <span className={styles.mChatTime}>11:14 AM</span>
        </div>
      </div>

      {/* Follow-up suggestions */}
      <div className={styles.mFollowUp}>
        <span className={styles.mFollowLabel}>{t('features.mock1.follow_up', 'Follow up:')}</span>
        <div className={styles.mChips}>
          <span className={styles.mChip}>{t('features.mock1.chip1', 'How do I file this claim?')}</span>
          <span className={styles.mChip}>{t('features.mock1.chip2', 'What documents do I need?')}</span>
        </div>
      </div>
    </div>

    {/* Input */}
    <div className={styles.mChatInput}>
      <span className={styles.mInputPlaceholder}>{t('features.mock1.input', 'Ask a follow-up question...')}</span>
      <div className={styles.mSendBtn}><Send size={12} strokeWidth={2.5} /></div>
    </div>
  </div>
);

/* ============================================
   MOCKUP: Claims Filing Wizard
   ============================================ */
const MockupClaimsWizard = ({ t }) => (
  <div className={styles.mockup}>
    {/* Header */}
    <div className={styles.mWizardHeader}>
      <div className={styles.mWizardAvatar}>
        <FileCheck size={14} strokeWidth={2} />
      </div>
      <div>
        <span className={styles.mChatName}>{t('features.mock2.name', 'Claims Assistant')}</span>
        <span className={styles.mChatOnline}>
          <span className={styles.mOnlineDot} />
          {t('features.mock2.status', 'Ready to help')}
        </span>
      </div>
    </div>

    <div className={styles.mWizardBody}>
      {/* Progress steps */}
      <div className={styles.mSteps}>
        <div className={`${styles.mStep} ${styles.mStepDone}`}>
          <div className={styles.mStepDot}><CheckCircle size={10} strokeWidth={3} /></div>
          <span>{t('features.mock2.step1', 'Incident Type')}</span>
        </div>
        <div className={styles.mStepLine} />
        <div className={`${styles.mStep} ${styles.mStepActive}`}>
          <div className={styles.mStepDotActive}>2</div>
          <span>{t('features.mock2.step2', 'Details')}</span>
        </div>
        <div className={styles.mStepLine} />
        <div className={styles.mStep}>
          <div className={styles.mStepDotEmpty}>3</div>
          <span>{t('features.mock2.step3', 'Documents')}</span>
        </div>
        <div className={styles.mStepLine} />
        <div className={styles.mStep}>
          <div className={styles.mStepDotEmpty}>4</div>
          <span>{t('features.mock2.step4', 'Review')}</span>
        </div>
      </div>

      {/* Selected incident */}
      <div className={styles.mSelectedIncident}>
        <div className={styles.mIncidentIcon}>
          <Car size={16} strokeWidth={2} />
        </div>
        <div className={styles.mIncidentInfo}>
          <span className={styles.mIncidentType}>{t('features.mock2.incident_type', 'Auto Collision')}</span>
          <span className={styles.mIncidentPolicy}>{t('features.mock2.incident_policy', 'TD Auto Insurance · TDI-2024-0847291')}</span>
        </div>
        <div className={styles.mIncidentCheck}>
          <CheckCircle size={14} strokeWidth={2.5} />
        </div>
      </div>

      {/* AI guidance message */}
      <div className={styles.mGuidanceBubble}>
        <p className={styles.mBubbleText}>
          {t('features.mock2.ai_guidance', 'I\'ve checked your policy. Auto collisions are covered under your comprehensive plan. Let me walk you through what we need:')}
        </p>

        <div className={styles.mChecklist}>
          <div className={styles.mCheckItem}>
            <CheckCircle size={12} strokeWidth={2.5} className={styles.mCheckDone} />
            <span>{t('features.mock2.check1', 'Incident type identified')}</span>
          </div>
          <div className={styles.mCheckItem}>
            <div className={styles.mCheckActive} />
            <span className={styles.mCheckBold}>{t('features.mock2.check2', 'Describe what happened')}</span>
          </div>
          <div className={styles.mCheckItem}>
            <div className={styles.mCheckEmpty} />
            <span className={styles.mCheckMuted}>{t('features.mock2.check3', 'Upload photos/evidence')}</span>
          </div>
          <div className={styles.mCheckItem}>
            <div className={styles.mCheckEmpty} />
            <span className={styles.mCheckMuted}>{t('features.mock2.check4', 'Review & submit')}</span>
          </div>
        </div>
      </div>

      {/* Question prompt */}
      <div className={styles.mQuestionPrompt}>
        <span className={styles.mQuestionLabel}>{t('features.mock2.question_label', 'When did the incident occur?')}</span>
        <div className={styles.mDateInput}>
          <Calendar size={12} strokeWidth={2} />
          <span>{t('features.mock2.date_placeholder', 'Select date and time')}</span>
        </div>
      </div>
    </div>

    {/* Footer */}
    <div className={styles.mWizardFooter}>
      <span className={styles.mWizardBack}>{t('features.mock2.back', '← Back')}</span>
      <div className={styles.mWizardNext}>
        <span>{t('features.mock2.next', 'Continue')}</span>
        <ChevronRight size={14} strokeWidth={2.5} />
      </div>
    </div>
  </div>
);

/* ============================================
   MOCKUP: Dashboard with Alerts
   ============================================ */
const MockupDashboardAlerts = ({ t }) => (
  <div className={styles.mockup}>
    {/* Dashboard header */}
    <div className={styles.mDashHeader}>
      <div className={styles.mDashWelcome}>
        <span className={styles.mDashGreeting}>{t('features.mock3.greeting', 'Good morning!')}</span>
        <span className={styles.mDashSub}>{t('features.mock3.sub', 'Here\'s what\'s happening with your insurance.')}</span>
      </div>
    </div>

    <div className={styles.mDashBody}>
      {/* Alert cards */}
      <div className={styles.mAlertCard} data-type="warning">
        <div className={styles.mAlertIcon} data-type="warning">
          <Clock size={14} strokeWidth={2.5} />
        </div>
        <div className={styles.mAlertContent}>
          <span className={styles.mAlertTitle}>{t('features.mock3.alert1_title', 'Auto Policy Renews in 28 Days')}</span>
          <span className={styles.mAlertSub}>{t('features.mock3.alert1_sub', 'TD Auto Insurance · Expires Feb 12, 2026')}</span>
        </div>
        <div className={styles.mAlertAction}>
          <span>{t('features.mock3.alert1_action', 'Review')}</span>
          <ChevronRight size={12} strokeWidth={2.5} />
        </div>
      </div>

      <div className={styles.mAlertCard} data-type="success">
        <div className={styles.mAlertIcon} data-type="success">
          <TrendingDown size={14} strokeWidth={2.5} />
        </div>
        <div className={styles.mAlertContent}>
          <span className={styles.mAlertTitle}>{t('features.mock3.alert2_title', 'Rate Drop Detected')}</span>
          <span className={styles.mAlertSub}>{t('features.mock3.alert2_sub', 'You could save ~$127/yr by switching home insurers')}</span>
        </div>
        <div className={styles.mAlertAction}>
          <span>{t('features.mock3.alert2_action', 'Compare')}</span>
          <ChevronRight size={12} strokeWidth={2.5} />
        </div>
      </div>

      <div className={styles.mAlertCard} data-type="info">
        <div className={styles.mAlertIcon} data-type="info">
          <Shield size={14} strokeWidth={2.5} />
        </div>
        <div className={styles.mAlertContent}>
          <span className={styles.mAlertTitle}>{t('features.mock3.alert3_title', 'Coverage Gap Found')}</span>
          <span className={styles.mAlertSub}>{t('features.mock3.alert3_sub', 'Your home policy doesn\'t include sewer backup coverage')}</span>
        </div>
        <div className={styles.mAlertAction}>
          <span>{t('features.mock3.alert3_action', 'Learn More')}</span>
          <ChevronRight size={12} strokeWidth={2.5} />
        </div>
      </div>

      {/* Coverage health score */}
      <div className={styles.mHealthCard}>
        <span className={styles.mHealthLabel}>{t('features.mock3.health_label', 'Coverage Health Score')}</span>
        <div className={styles.mHealthGauge}>
          <div className={styles.mGaugeTrack}>
            <div className={styles.mGaugeFill} style={{ width: '74%' }} />
          </div>
          <span className={styles.mGaugeValue}>74<span className={styles.mGaugeMax}>/100</span></span>
        </div>
        <div className={styles.mHealthTags}>
          <span className={styles.mHealthTag} data-type="success">{t('features.mock3.tag_good', '3 policies active')}</span>
          <span className={styles.mHealthTag} data-type="warning">{t('features.mock3.tag_warn', '1 gap detected')}</span>
          <span className={styles.mHealthTag} data-type="info">{t('features.mock3.tag_info', '1 renewal soon')}</span>
        </div>
      </div>

      {/* Quick policy summary */}
      <div className={styles.mPolicySummary}>
        <span className={styles.mSummaryTitle}>{t('features.mock3.summary_title', 'Your Policies')}</span>
        <div className={styles.mSummaryRow}>
          <div className={styles.mSummaryIcon}><Car size={12} strokeWidth={2} /></div>
          <span className={styles.mSummaryName}>TD Auto</span>
          <span className={styles.mSummaryStatus} data-type="warning">{t('features.mock3.renewing', 'Renewing')}</span>
        </div>
        <div className={styles.mSummaryRow}>
          <div className={styles.mSummaryIcon}><HomeIcon size={12} strokeWidth={2} /></div>
          <span className={styles.mSummaryName}>Intact Home</span>
          <span className={styles.mSummaryStatus} data-type="success">{t('features.mock3.active', 'Active')}</span>
        </div>
        <div className={styles.mSummaryRow}>
          <div className={styles.mSummaryIcon}><Heart size={12} strokeWidth={2} /></div>
          <span className={styles.mSummaryName}>Manulife Life</span>
          <span className={styles.mSummaryStatus} data-type="success">{t('features.mock3.active', 'Active')}</span>
        </div>
      </div>
    </div>
  </div>
);

/* ============================================
   MAIN FEATURES COMPONENT
   ============================================ */
const Features = () => {
  const { t, parseHTML } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = React.useState(false);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard/policies');
    } else {
      setShowAuthModal(true);
    }
  };

  const FEATURES = [
    {
      id: 'ask-before-claim',
      icon: MessageCircle,
      label: t('features.f1.label', 'AI Policy Advisor'),
      title: t('features.f1.title', 'Confused about coverage? Just ask.'),
      description: t('features.f1.description', 'Before you call your insurance company, ask us. Our AI reads your actual policy and gives you straight answers — what\'s covered, what\'s not, and what your deductible really means. No jargon. No runaround.'),
      highlight: t('features.f1.highlight', '5 free questions/month · Unlimited on paid plans'),
      ctaText: isAuthenticated
        ? t('features.f1.cta_auth', 'Go to Wallet')
        : t('features.f1.cta', 'Try AI Advisor'),
      ctaAction: handleGetStarted,
      mockup: 'chat',
      reverse: false,
      bgImage: '/images/feature-bg-couple.jpg',
    },
    {
      id: 'claims-ready',
      icon: FileCheck,
      label: t('features.f2.label', 'Claims Assistant'),
      title: t('features.f2.title', 'File claims without the headache.'),
      description: t('features.f2.description', 'Step-by-step guidance through the claims process. Know what documents you need, whether your incident is covered, and what to expect — before you file. Walk into your claim prepared, not panicked.'),
      highlight: t('features.f2.highlight', 'Available on Smart Shopper and above'),
      ctaText: isAuthenticated
        ? t('features.f2.cta_auth', 'Go to Wallet')
        : t('features.f2.cta', 'Start a Claim'),
      ctaAction: handleGetStarted,
      mockup: 'claims',
      reverse: true,
      bgImage: null,
    },
    {
      id: 'stay-informed',
      icon: Bell,
      label: t('features.f3.label', 'Smart Alerts'),
      title: t('features.f3.title', 'Never miss a renewal. Never overpay.'),
      description: t('features.f3.description', 'Get notified 90, 60, and 30 days before your policy renews. Our AI monitors rate changes and flags when you might be paying too much — so you can switch or negotiate before it\'s too late.'),
      highlight: t('features.f3.highlight', 'Included in every plan'),
      ctaText: isAuthenticated
        ? t('features.f3.cta_auth', 'Go to Wallet')
        : t('features.f3.cta', 'Set Up Alerts'),
      ctaAction: handleGetStarted,
      mockup: 'dashboard',
      reverse: false,
      bgImage: null,
    },
  ];

  const renderMockup = (type) => {
    switch (type) {
      case 'chat': return <MockupPolicyChat t={t} />;
      case 'claims': return <MockupClaimsWizard t={t} />;
      case 'dashboard': return <MockupDashboardAlerts t={t} />;
      default: return null;
    }
  };

  return (
    <section id="features" className={styles.section}>
      {/* Section Title */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>
          {t('features.section_title_start', 'Your wallet just got ')}
          <span className={styles.highlight}>
            {t('features.section_title_end', 'a lot smarter.')}
          </span>
        </h2>
        <p className={styles.sectionSubtitle}>
          AI-powered insights that help you save money, avoid gaps, and actually understand your coverage.
        </p>
      </div>

      {FEATURES.map((feature, index) => {
        const IconComponent = feature.icon;
        return (
          <div
            key={feature.id}
            className={`${styles.featureBlock} ${feature.reverse ? styles.reverse : ''} ${index % 2 === 1 ? styles.altBg : ''}`}
          >
            {feature.bgImage && (
              <div className={styles.bgImageWrapper}>
                <img src={feature.bgImage} alt="" className={styles.bgImage} loading="lazy" />
                <div className={styles.bgImageOverlay} />
              </div>
            )}

            <div className={styles.featureInner}>
              <ScrollAnimation>
                <div className={styles.textSide}>
                  <div className={styles.featureLabel}>
                    <IconComponent size={16} strokeWidth={2.5} />
                    <span>{feature.label}</span>
                  </div>
                  <h3 className={styles.featureTitle}>{feature.title}</h3>
                  <p className={styles.featureDescription}>{feature.description}</p>
                  {/* <div className={styles.featureHighlight}>
                    <Shield size={14} strokeWidth={2} />
                    <span>{feature.highlight}</span>
                  </div> */}
                  
                  {/* CTA button - hidden on mobile, shown on tablet+ */}
                  <button
                    className={`${styles.featureCta} ${styles.featureCtaDesktop}`}
                    onClick={feature.ctaAction}
                  >
                    <span>{feature.ctaText}</span>
                    <ArrowRight size={16} strokeWidth={2.5} />
                  </button>
                </div>
              </ScrollAnimation>

              <ScrollAnimation delay={200}>
                <div className={styles.screenshotSide}>
                  <div className={styles.deviceFrame}>
                    <div className={styles.deviceBar}>
                      <div className={styles.deviceDots}>
                        <span /><span /><span />
                      </div>
                      <div className={styles.deviceUrl}>allegoryinsurance.com</div>
                      <div className={styles.deviceDotsSpacer} />
                    </div>
                    <div className={styles.deviceScreen}>
                      {renderMockup(feature.mockup)}
                    </div>
                  </div>
                  
                  {/* CTA button - shown on mobile, hidden on tablet+ */}
                  <button
                    className={`${styles.featureCta} ${styles.featureCtaMobile}`}
                    onClick={feature.ctaAction}
                  >
                    <span>{feature.ctaText}</span>
                    <ArrowRight size={16} strokeWidth={2.5} />
                  </button>
                </div>
              </ScrollAnimation>
            </div>
          </div>
        );
      })}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialType="sign_up"
      />
    </section>
  );
};

export default Features;
